import {
  type GroupRegistryDocument,
  parseGroupRegistryDocument,
} from "./groupRegistry.document";

export const GROUP_REGISTRY_CHANNEL_NAME = "workspace-global-groups-v3";

export interface GroupRegistryMessage {
  writerId: string;
  document: GroupRegistryDocument;
}

export interface GroupRegistryChannel {
  post: (message: GroupRegistryMessage) => void;
  subscribe: (listener: (message: GroupRegistryMessage) => void) => () => void;
  close: () => void;
}

type Endpoint = {
  listeners: Set<(message: GroupRegistryMessage) => void>;
};

const buses = new Map<string, Set<Endpoint>>();

const parseMessage = (value: unknown): GroupRegistryMessage | undefined => {
  if (
    typeof value !== "object" ||
    value === null ||
    !("writerId" in value) ||
    typeof value.writerId !== "string" ||
    !("document" in value)
  ) {
    return undefined;
  }
  const document = parseGroupRegistryDocument(value.document);
  return document ? { writerId: value.writerId, document } : undefined;
};

export const createInProcessGroupRegistryChannel = (
  name = GROUP_REGISTRY_CHANNEL_NAME,
): GroupRegistryChannel => {
  const endpoint: Endpoint = { listeners: new Set() };
  const endpoints = buses.get(name) ?? new Set();
  endpoints.add(endpoint);
  buses.set(name, endpoints);

  return {
    post: (value) => {
      const message = parseMessage(value);
      if (!message) {
        return;
      }
      for (const peer of buses.get(name) ?? []) {
        if (peer === endpoint) {
          continue;
        }
        for (const listener of [...peer.listeners]) {
          listener(message);
        }
      }
    },
    subscribe: (listener) => {
      endpoint.listeners.add(listener);
      return () => endpoint.listeners.delete(listener);
    },
    close: () => {
      endpoint.listeners.clear();
      const endpoints = buses.get(name);
      endpoints?.delete(endpoint);
      if (endpoints?.size === 0) {
        buses.delete(name);
      }
    },
  };
};

const createBroadcastGroupRegistryChannel = (
  name: string,
): GroupRegistryChannel => {
  const channel = new BroadcastChannel(name);
  const listeners = new Set<(message: GroupRegistryMessage) => void>();
  const onMessage = (event: MessageEvent<unknown>) => {
    const message = parseMessage(event.data);
    if (!message) {
      return;
    }
    for (const listener of [...listeners]) {
      listener(message);
    }
  };
  channel.addEventListener("message", onMessage);

  return {
    post: (message) => {
      try {
        channel.postMessage(message);
      } catch {
        // Storage events or the next locked mutation reconcile close races.
      }
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    close: () => {
      listeners.clear();
      channel.removeEventListener("message", onMessage);
      channel.close();
    },
  };
};

export const createGroupRegistryChannel = (
  name = GROUP_REGISTRY_CHANNEL_NAME,
): GroupRegistryChannel =>
  typeof BroadcastChannel === "function"
    ? createBroadcastGroupRegistryChannel(name)
    : createInProcessGroupRegistryChannel(name);
